export interface ManualEventCategoryInput {
  lc_id: string;
  ec_is_home: boolean;
}

interface LeagueCategoryLike {
  lc_id?: string | null;
  lc_name?: string | null;
  lc_abbrev?: string | null;
  lc_ticketmaster_name?: string | null;
  lc_alternate_name?: string[] | string | null;
  lc_market_name?: string | null;
}

const normalizeCategoryName = (value?: string | null) =>
  (value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getCategoryNameCandidates = (category?: LeagueCategoryLike | null) => {
  const values = [
    category?.lc_name,
    category?.lc_abbrev,
    category?.lc_ticketmaster_name,
    category?.lc_market_name,
  ];

  const alternateNames = category?.lc_alternate_name;
  if (Array.isArray(alternateNames)) {
    values.push(...alternateNames);
  } else if (typeof alternateNames === "string") {
    values.push(alternateNames);
  }

  return values
    .filter((value): value is string => Boolean(value))
    .map((value) => normalizeCategoryName(value));
};

const getCategoryTokens = (category?: LeagueCategoryLike | null) => {
  return getCategoryNameCandidates(category).flatMap((value) => value.split(" ").filter(Boolean));
};

const findMatchingCategory = (
  sourceName?: string | null,
  leagueCategories: LeagueCategoryLike[] = [],
  allowPartialMatch = false
): LeagueCategoryLike | undefined => {
  const normalizedSourceName = normalizeCategoryName(sourceName);
  if (!normalizedSourceName) {
    return undefined;
  }

  const exactMatch = leagueCategories.find((category) =>
    getCategoryNameCandidates(category).includes(normalizedSourceName)
  );
  if (exactMatch) {
    return exactMatch;
  }

  if (!allowPartialMatch) {
    return undefined;
  }

  const sourceTokens = normalizedSourceName.split(" ").filter(Boolean);
  if (sourceTokens.length === 0) {
    return undefined;
  }

  const partialMatches = leagueCategories.filter((category) => {
    const categoryTokens = new Set(getCategoryTokens(category));
    if (categoryTokens.size === 0) {
      return false;
    }

    const sourceTokenMatch = sourceTokens.every((token) => categoryTokens.has(token));
    const categoryTokenMatch = [...categoryTokens].every((token) => sourceTokens.includes(token));

    return sourceTokenMatch || categoryTokenMatch;
  });

  return partialMatches.length === 1 ? partialMatches[0] : undefined;
};

const containsPhrase = (haystack: string, phrase: string) =>
  Boolean(haystack && phrase) && ` ${haystack} `.includes(` ${phrase} `);

const isPhrasePrefix = (haystack: string, phrase: string) =>
  haystack === phrase || haystack.startsWith(`${phrase} `);

const performerNameCandidates = (
  category: LeagueCategoryLike | null | undefined,
  leagueName?: string | null,
) => {
  const leagueKey = normalizeCategoryName(leagueName);
  return getCategoryNameCandidates(category).filter(
    (name) => name && name !== leagueKey && name.length >= 2,
  );
};

const pickLongestUniqueCategory = (
  matches: Array<{ category: LeagueCategoryLike; score: number }>,
): LeagueCategoryLike | undefined => {
  if (matches.length === 0) return undefined;

  const bestScore = Math.max(...matches.map((match) => match.score));
  const top = matches.filter((match) => match.score === bestScore);
  const uniqueIds = new Set(top.map((match) => match.category.lc_id).filter(Boolean));
  return uniqueIds.size === 1 ? top[0].category : undefined;
};

export const resolveConcertPerformerCategory = (
  eventName?: string | null,
  displayName?: string | null,
  leagueName?: string | null,
  leagueCategories: LeagueCategoryLike[] = [],
): LeagueCategoryLike | undefined => {
  const haystacks = [eventName, displayName].map(normalizeCategoryName).filter(Boolean);
  if (haystacks.length === 0) return undefined;

  const scoreCategories = (matcher: (haystack: string, phrase: string) => boolean) =>
    leagueCategories
      .map((category) => {
        const score = performerNameCandidates(category, leagueName).reduce((best, name) => {
          const matched = haystacks.some((haystack) => matcher(haystack, name));
          return matched ? Math.max(best, name.length) : best;
        }, 0);
        return { category, score };
      })
      .filter((match) => match.score > 0);

  const prefixMatch = pickLongestUniqueCategory(scoreCategories(isPhrasePrefix));
  if (prefixMatch) return prefixMatch;

  return pickLongestUniqueCategory(scoreCategories(containsPhrase));
};

export const resolveConcertArtistName = (
  eventName?: string | null,
  displayName?: string | null,
  leagueName?: string | null,
) => {
  const leagueKey = normalizeCategoryName(leagueName);
  const event = (eventName || "").trim();
  const display = (displayName || "").trim();
  const displayKey = normalizeCategoryName(display);
  const eventKey = normalizeCategoryName(event);

  if (displayKey && displayKey !== leagueKey && displayKey !== eventKey) {
    return display;
  }

  const colonIndex = event.indexOf(":");
  const beforeColon = colonIndex > 0 ? event.slice(0, colonIndex).trim() : event;
  if (normalizeCategoryName(beforeColon) && normalizeCategoryName(beforeColon) !== leagueKey) {
    return beforeColon;
  }

  return event;
};

export const buildConcertCategoryInputs = (
  leagueCategoryId?: string | null,
  performerCategoryId?: string | null,
): ManualEventCategoryInput[] => {
  if (performerCategoryId) {
    return [{ lc_id: performerCategoryId, ec_is_home: true }];
  }

  if (leagueCategoryId) {
    return [{ lc_id: leagueCategoryId, ec_is_home: true }];
  }

  return [];
};

export const resolveLeagueCategoryId = (
  leagueName?: string | null,
  leagueCategories: LeagueCategoryLike[] = []
): string | undefined => {
  const matchingCategory = findMatchingCategory(leagueName, leagueCategories, false);
  if (matchingCategory?.lc_id) {
    return matchingCategory.lc_id;
  }

  if (leagueCategories.length === 1) {
    const fallbackId = leagueCategories[0].lc_id;
    if (fallbackId == null) {
      return undefined;
    }
    return fallbackId;
  }

  return undefined;
};

export const buildManualEventCategoryInputs = ({
  leagueCategoryId,
  leagueCategories = [],
  homeTeamName,
  awayTeamName,
}: {
  leagueCategoryId?: string | null;
  leagueCategories?: LeagueCategoryLike[];
  homeTeamName?: string | null;
  awayTeamName?: string | null;
}): ManualEventCategoryInput[] => {
  const categoryInputs: ManualEventCategoryInput[] = [];

  if (leagueCategoryId) {
    categoryInputs.push({ lc_id: leagueCategoryId, ec_is_home: true });
  }

  const addResolvedCategory = (teamName?: string | null, isHome: boolean = false) => {
    const trimmedName = teamName?.trim();
    if (!trimmedName) return;

    const matchingCategory = findMatchingCategory(trimmedName, leagueCategories, true);
    if (matchingCategory?.lc_id) {
      categoryInputs.push({ lc_id: matchingCategory.lc_id, ec_is_home: isHome });
    }
  };

  addResolvedCategory(homeTeamName, true);
  addResolvedCategory(awayTeamName, false);

  const uniqueCategoryInputs = categoryInputs.filter((input, index, array) =>
    array.findIndex((candidate) => candidate.lc_id === input.lc_id && candidate.ec_is_home === input.ec_is_home) === index
  );

  return uniqueCategoryInputs;
};
